import React, { useEffect, useState } from "react";
import { Trophy, Save } from "lucide-react";
import { toast } from "sonner";
import ResourceAdmin from "../../components/admin/ResourceAdmin";
import EvidenciaDeletePanel, { StatusBadge } from "../../components/admin/EvidenciaDeletePanel";
import { adminApi, apiError } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function GraduacionAdmin() {
  const [cfg, setCfg] = useState({ min_graduacion: 1891, max_excelencia: 2071 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getGradConfig().then(setCfg).catch(() => {});
  }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      await adminApi.updateGradConfig({ min_graduacion: Number(cfg.min_graduacion), max_excelencia: Number(cfg.max_excelencia) });
      toast.success("Umbrales actualizados");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#0A1C30] flex items-center justify-center">
          <Trophy className="w-6 h-6 text-gold" />
        </span>
        <div>
          <h1 className="text-3xl font-extrabold text-[#0A1C30]">Puntos Graduación Supera</h1>
          <p className="text-[#6B7688]">Define actividades, puntos y reconocimientos para la graduación.</p>
        </div>
      </div>

      {/* Thresholds */}
      <div className="mt-6 bg-white rounded-2xl ring-1 ring-black/5 p-6">
        <h2 className="text-lg font-extrabold text-[#0A1C30]">Umbrales de graduación</h2>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Mínimo para graduarse</Label>
            <Input type="number" value={cfg.min_graduacion} onChange={(e) => setCfg((c) => ({ ...c, min_graduacion: e.target.value }))} className="w-48" />
          </div>
          <div>
            <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Excelencia (máximo)</Label>
            <Input type="number" value={cfg.max_excelencia} onChange={(e) => setCfg((c) => ({ ...c, max_excelencia: e.target.value }))} className="w-48" />
          </div>
          <button onClick={saveCfg} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-2.5 transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> Guardar
          </button>
        </div>
      </div>

      <Tabs defaultValue="actividades" className="mt-8">
        <TabsList className="bg-white ring-1 ring-black/5">
          <TabsTrigger value="actividades">Actividades y puntos</TabsTrigger>
          <TabsTrigger value="reconocimientos">Reconocimientos</TabsTrigger>
          <TabsTrigger value="evidencias">Evidencias</TabsTrigger>
        </TabsList>

        <TabsContent value="actividades" className="mt-6">
          <ResourceAdmin
            title="Actividades"
            subtitle="Cada actividad con su valor en puntos. El usuario suma puntos al validarse su evidencia."
            resource={adminApi.gradActividades()}
            defaults={{ nombre: "", cantidad_minima: 1, puntos_unitarios: 1, puntos_minimos: 1, orden: 99, activo: true, visible_invitados: false }}
            fields={[
              { name: "nombre", label: "Actividad", type: "text", placeholder: "Ej. Video Express" },
              { name: "cantidad_minima", label: "Cantidad mínima", type: "number" },
              { name: "puntos_unitarios", label: "Puntos por evidencia", type: "number" },
              { name: "puntos_minimos", label: "Puntos mínimos (total)", type: "number" },
              { name: "orden", label: "Orden", type: "number" },
              { name: "activo", label: "Estado", type: "switch" },
              { name: "visible_invitados", label: "Visible para invitados", type: "switch" },
            ]}
            columns={[
              { name: "orden", label: "#" },
              { name: "nombre", label: "Actividad", render: (i) => <span className="font-bold text-[#0A1C30]">{i.nombre}</span> },
              { name: "cantidad_minima", label: "Cant. mín." },
              { name: "puntos_unitarios", label: "Pts/ev.", render: (i) => <span className="font-extrabold text-[#E9A400]">{i.puntos_unitarios}</span> },
              { name: "puntos_minimos", label: "Pts mín." },
              { name: "activo", label: "Estado", render: (i) => (
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${i.activo !== false ? "bg-[#E2F6EB] text-[#22A860]" : "bg-[#EDF0F4] text-[#6B7688]"}`}>
                  {i.activo !== false ? "Activa" : "Inactiva"}
                </span>
              ) },
            ]}
          />
        </TabsContent>

        <TabsContent value="reconocimientos" className="mt-6">
          <ResourceAdmin
            title="Reconocimientos"
            subtitle="Medallas de liderazgo otorgadas automáticamente al alcanzar los puntos."
            resource={adminApi.gradReconocimientos()}
            defaults={{ nombre: "", puntos: 100, descripcion: "", color: "#F5C518", icono: "Trophy", orden: 99 }}
            fields={[
              { name: "nombre", label: "Nombre del reconocimiento", type: "text", placeholder: "Ej. Líder Inspirador" },
              { name: "puntos", label: "Puntos requeridos", type: "number" },
              { name: "descripcion", label: "Descripción", type: "textarea" },
              { name: "color", label: "Color (hex)", type: "text", placeholder: "#F5C518" },
              { name: "icono", label: "Icono (lucide)", type: "text", placeholder: "Trophy" },
            ]}
            columns={[
              { name: "puntos", label: "Puntos", render: (i) => <span className="font-extrabold text-[#0A1C30]">{i.puntos}</span> },
              { name: "nombre", label: "Reconocimiento", render: (i) => (
                <span className="inline-flex items-center gap-2 font-bold text-[#0A1C30]">
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: i.color }} /> {i.nombre}
                </span>
              ) },
              { name: "descripcion", label: "Descripción" },
            ]}
          />
        </TabsContent>

        <TabsContent value="evidencias" className="mt-6">
          <EvidenciaDeletePanel
            testid="grad-evid"
            title="Evidencias de Graduación"
            subtitle="Repositorio de evidencias de actividades de graduación. El Super Admin puede eliminarlas para liberar espacio sin restar puntos al alumno."
            fetchList={(status, page) => adminApi.listGradEvidencias(status, page)}
            deleteOne={(id) => adminApi.deleteGradEvidencia(id)}
            deleteBulk={(payload) => adminApi.deleteGradEvidenciasBulk(payload)}
            columns={[
              { label: "Usuario", render: (i) => <span className="font-bold text-[#0A1C30]">{i.user_name || i.user_email}</span> },
              { label: "Programa", render: (i) => i.programa || "—" },
              { label: "Actividad", render: (i) => i.actividad_nombre || "—" },
              { label: "Fecha", render: (i) => <span className="text-[#6B7688]">{i.reflexion_fecha || "—"}</span> },
              { label: "Puntos", render: (i) => <span className="font-extrabold text-[#E9A400]">{i.puntos ?? 0}</span> },
              { label: "Estado", render: (i) => <StatusBadge status={i.status} /> },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}